import {
  FC,
  createContext,
  useEffect,
  useCallback,
  useState,
  useMemo,
  PropsWithChildren,
  useContext,
} from 'react';
import { SetValueAction, useStorage } from '../hooks/useStorage';
import { useSessionToken } from './SessionContext';
import JsonWebTokenService from '../services/JsonWebTokenService';
import { ISessionToken, JsonWebToken } from '../services/JsonWebTokenService/types';
import SessionService from '../services/SessionService';
import { useCallbackWithLoading } from '../hooks/useCallbackWithLoading';
import { SignUpProfilePayload, SignUpResponse } from '../services/SessionService/types';
import { IEvent } from '../services/EventService.ts/types';
import { UserRole } from '../services/UserService/types';

export enum AuthStatus {
	NOT_AUTHENTICATED = 1,
  EMAIL_NOT_VERIFIED = 2,
	AUTHENTICATED = 3,
	AUTHENTICATION_EXPIRED = 4,
}

export interface IAuthContext {
	status: AuthStatus;
	sessionToken?: ISessionToken;
	setSessionToken: SetValueAction<ISessionToken | undefined>;
	resetAuth: () => Promise<void>;
	getSessionToken: (email: string, password: string) => Promise<void>;
	signUpSession: (payload: SignUpProfilePayload) => Promise<SignUpResponse>;
  selectedEvent: IEvent | undefined;
  setSelectedEvent: SetValueAction<IEvent | undefined>;
  isEmailVerified: boolean;
  sendResetCode: (email: string) => Promise<void>;
  verifyResetCode: (email: string, code: string) => Promise<{ resetToken: string }>;
  resetPassword: (email: string, token: string, password: string) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<void>;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

const isTokenValid = (token?: ISessionToken): boolean => {
  if (!token) {
    return false;
  }
  return JsonWebTokenService.isValid(token);
};

export const AuthProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.AUTHENTICATED);
  const { sessionToken, setSessionToken, deleteSessionToken } = useSessionToken();
  const [selectedEvent, setSelectedEvent, deleteSelectedEvent] = useStorage<IEvent>('selected-event');

  const isSessionTokenValid: boolean = useMemo(() => isTokenValid(sessionToken), [sessionToken]);
  const isEmailVerified: boolean = useMemo(() => sessionToken?.user?.isEmailVerified || false, [sessionToken]);

  const resetAuth = useCallback(async () => {
    deleteSessionToken();
    setSessionToken(undefined);
    deleteSelectedEvent();
  }, [deleteSelectedEvent, deleteSessionToken, setSessionToken]);

  const getSessionToken = useCallbackWithLoading(async (email: string, password: string) => {
    const newSessionToken = await SessionService.login(email, password);
    const { user: { isEmailVerified, role }} = newSessionToken;
    if (!isEmailVerified && role === UserRole.User) {
      throw new Error('Email not verified');
    }
    setSessionToken(newSessionToken);
  }, [setSessionToken]);

  const signUpSession = useCallbackWithLoading(async (payload: SignUpProfilePayload) => {
    const response = await SessionService.signUp(payload);
    return response;
  }, []);

  const sendResetCode = useCallbackWithLoading(async (email: string) => {
    await SessionService.sendResetCode(email);
  }, []);

  const verifyResetCode = useCallbackWithLoading(async (email: string, code: string) => {
    return await SessionService.verifyResetCode(email, code);
  }, []);

  const resetPassword = useCallbackWithLoading(async (email: string, token: string, password: string) => {
    await SessionService.resetPassword(email, token, password);
  }, []);

  const sendVerificationCode = useCallbackWithLoading(async (email: string) => {
    await SessionService.sendVerificationCode(email);
  }, []);

  const verifyEmailCode = useCallbackWithLoading(async (email: string, code: string) => {
    const response = await SessionService.verifyEmailCode(email, code);
    if (response.token && response.user) {
      setSessionToken({ ...JsonWebTokenService.parse<JsonWebToken>(response.token), user: response.user });
    }
  }, [setSessionToken]);

  useEffect(() => {
    if (sessionToken && sessionToken.user && isSessionTokenValid && (sessionToken.user.role === UserRole.Admin || (sessionToken.user.role === UserRole.User && isEmailVerified))) {
      setStatus(AuthStatus.AUTHENTICATED);
    } else if (sessionToken && !isEmailVerified && sessionToken.user.role === UserRole.User) {
      setStatus(AuthStatus.EMAIL_NOT_VERIFIED);
    } else if (sessionToken && !isSessionTokenValid) {
      setStatus(AuthStatus.AUTHENTICATION_EXPIRED);
      resetAuth();
    } else {
      setStatus(AuthStatus.NOT_AUTHENTICATED);
    }
  }, [sessionToken, isSessionTokenValid, resetAuth, isEmailVerified]);

  const value = useMemo(
    () => ({
      status,
      sessionToken,
      setSessionToken,
      resetAuth,
      getSessionToken,
      signUpSession,
      selectedEvent,
      setSelectedEvent,
      isEmailVerified,
      sendResetCode,
      verifyResetCode,
      resetPassword,
      sendVerificationCode,
      verifyEmailCode,
    }),
    [
      status,
      sessionToken,
      setSessionToken,
      resetAuth,
      getSessionToken,
      signUpSession,
      selectedEvent,
      setSelectedEvent,
      isEmailVerified,
      sendResetCode,
      verifyResetCode,
      resetPassword,
      sendVerificationCode,
      verifyEmailCode,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthentication = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthentication must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
